import { Helmet } from 'react-helmet-async'
import {
	SITE_NAME,
	SITE_URL,
	SITE_DESCRIPTION,
	SITE_DEFAULT_OG,
	SITE_LOCALE,
	SITE_LOCALE_ALT,
	buildCanonical
} from '@constants'

export interface SEOProps {
	title?: string
	description?: string
	path?: string
	image?: string
	type?: 'website' | 'article' | 'product' | 'event'
	noindex?: boolean
	keywords?: string[]
	publishedTime?: string
	modifiedTime?: string
	jsonLd?: object | object[]
}

export const SEO = ({
	title,
	description = SITE_DESCRIPTION,
	path = '/',
	image = SITE_DEFAULT_OG,
	type = 'website',
	noindex = false,
	keywords,
	publishedTime,
	modifiedTime,
	jsonLd
}: SEOProps) => {
	const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME
	const canonical = buildCanonical(path)
	const ogImage =
		image && image.startsWith('http') ? image : `${SITE_URL}${image}`
	const ldArray = jsonLd
		? Array.isArray(jsonLd)
			? jsonLd
			: [jsonLd]
		: []

	return (
		<Helmet prioritizeSeoTags>
			<title>{fullTitle}</title>
			<meta name="description" content={description} />
			{keywords && keywords.length > 0 && (
				<meta name="keywords" content={keywords.join(', ')} />
			)}
			{noindex ? (
				<meta name="robots" content="noindex, nofollow" />
			) : (
				<meta
					name="robots"
					content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
				/>
			)}
			<link rel="canonical" href={canonical} />

			{/* Open Graph */}
			<meta property="og:site_name" content={SITE_NAME} />
			<meta property="og:title" content={fullTitle} />
			<meta property="og:description" content={description} />
			<meta property="og:url" content={canonical} />
			<meta property="og:type" content={type} />
			<meta property="og:locale" content={SITE_LOCALE} />
			<meta property="og:locale:alternate" content={SITE_LOCALE_ALT} />
			<meta property="og:image" content={ogImage} />
			<meta property="og:image:alt" content={title || SITE_NAME} />
			{publishedTime && (
				<meta property="article:published_time" content={publishedTime} />
			)}
			{modifiedTime && (
				<meta property="article:modified_time" content={modifiedTime} />
			)}

			{/* Twitter */}
			<meta name="twitter:card" content="summary_large_image" />
			<meta name="twitter:title" content={fullTitle} />
			<meta name="twitter:description" content={description} />
			<meta name="twitter:image" content={ogImage} />

			{ldArray.map((schema, i) => (
				<script key={`ld-${i}`} type="application/ld+json">
					{JSON.stringify(schema)}
				</script>
			))}
		</Helmet>
	)
}
